/**
 * Pagination — the one pager every table uses.
 *
 * Jesse, 2026-10-30: the Audit Log only had Previous and Next, with no way to jump
 * to the first or last page, to a page number, or to show more rows. It was every
 * table, because they all share this component, so the fix is here once:
 *
 * - First / Previous / page box / Next / Last. The page box takes a number and goes
 *   there on Enter or when it loses focus; anything out of range is clamped.
 * - "Rows per page", when the page passes `onPageSizeChange` (remember the choice
 *   with `usePageSize`). The options suit every list endpoint's limit (≥ 100).
 * - "Showing 51–100 of 1,234", when the page passes `total`.
 * - A page past the end (a remembered page 7 of a list that now has 2 pages, or a
 *   filter that shrank the list) moves to the last page by itself. Before this, the
 *   Audit Log could say "85 entries" over an empty table.
 */
import { useEffect, useState } from 'react'
import { PAGE_SIZE_OPTIONS } from '../hooks/useSessionState'

interface PaginationProps {
  page: number
  totalPages: number
  onPageChange: (newPage: number) => void
  /** Total rows across all pages, for "Showing X–Y of Z". */
  total?: number
  pageSize?: number
  /** Offer "Rows per page". The caller should also go back to page 1. */
  onPageSizeChange?: (size: number) => void
}

function NavButton({ label, disabled, onClick, children }: {
  label: string
  disabled: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="btn btn-secondary px-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  )
}

export function Pagination({
  page, totalPages, onPageChange, total, pageSize, onPageSizeChange,
}: PaginationProps) {
  const [draft, setDraft] = useState(String(page))
  useEffect(() => { setDraft(String(page)) }, [page])

  // Past the end: go to the last page that exists.
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) onPageChange(totalPages)
  }, [page, totalPages, onPageChange])

  const showSize = !!onPageSizeChange && !!pageSize && (total ?? 0) > PAGE_SIZE_OPTIONS[0]
  if (totalPages <= 1 && !showSize) return null

  function go(target: number) {
    const next = Math.min(Math.max(1, Math.trunc(target) || 1), Math.max(1, totalPages))
    setDraft(String(next))
    if (next !== page) onPageChange(next)
  }

  const first = total && pageSize ? (page - 1) * pageSize + 1 : null
  const last = total && pageSize ? Math.min(page * pageSize, total) : null

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-700">
      <div className="min-w-[8rem] text-gray-500">
        {first && last && total ? (
          <>Showing {first.toLocaleString()}–{last.toLocaleString()} of {total.toLocaleString()}</>
        ) : null}
      </div>

      {totalPages > 1 && (
        <nav aria-label="Pages" className="flex flex-wrap items-center gap-1.5">
          <NavButton label="First page" disabled={page <= 1} onClick={() => go(1)}>«</NavButton>
          <NavButton label="Previous page" disabled={page <= 1} onClick={() => go(page - 1)}>‹ Prev</NavButton>
          <label className="flex items-center gap-1.5 px-1">
            <span>Page</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={totalPages}
              aria-label={`Page number, 1 to ${totalPages}`}
              className="input w-16 py-1 text-center text-sm"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => go(Number(draft))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); go(Number(draft)) }
              }}
            />
            <span>of {totalPages.toLocaleString()}</span>
          </label>
          <NavButton label="Next page" disabled={page >= totalPages} onClick={() => go(page + 1)}>Next ›</NavButton>
          <NavButton label="Last page" disabled={page >= totalPages} onClick={() => go(totalPages)}>»</NavButton>
        </nav>
      )}

      <div className="min-w-[8rem] flex justify-end">
        {showSize && (
          <label className="flex items-center gap-1.5">
            <span className="text-gray-500">Rows per page</span>
            <select
              className="filter-control"
              value={pageSize}
              onChange={(e) => onPageSizeChange!(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        )}
      </div>
    </div>
  )
}
